/**
 * TerrainRoot — Web-Mercator-Kachel-Terrain als EIN SceneNode.
 *
 * Aufbau nach dem EnvironmentRoot-Muster: dieser Node besitzt eine
 * THREE.Group; die Kacheln selbst sind bewusst KEINE SceneNodes, sondern
 * rohe THREE.Meshes unter der Group — hunderte streamende Kacheln sollen
 * weder Transform-Interpolation noch SceneNodeManager-Buchhaltung tragen.
 *
 * LOD: klassisches Chunked-LOD am Quadtree. Eine Kachel teilt sich, wenn
 * ihre projizierte Groesse (Kachelspanne/Distanz) den Schwellwert reisst;
 * die ELTERN-Kachel bleibt sichtbar, bis alle vier Kinder fertig geladen
 * sind — nie Loecher, nie weisses Blitzen. Risse zwischen LOD-Stufen
 * verdeckt ein Skirt-Ring je Kachel (Rand um 4 % der Spanne abgesenkt).
 *
 * Hoehenabfrage: getHeightMeters()/getWorldY() — bilinear aus dem feinsten
 * geladenen Hoehenfeld. Das ist der Ersatz fuer MapPinsApp.terrainLocalY
 * (BVH-Raycast); Pins/Routen/Kamera-Kollision koennen direkt andocken.
 */
import * as THREE from 'three';
import { SceneNode } from '../core/SceneNode';
import { MercatorProjection, type TileAddress } from './MercatorProjection';
import { TerrainMaterialManager } from './TerrainMaterialManager';
import { TerrainTileSource, type Hoehenfeld } from './TerrainTileSource';

export interface TerrainRootConfig {
    projection: MercatorProjection;
    source: TerrainTileSource;
    /** Geo-Abdeckung W,S,E,N */
    bounds: { minLon: number; minLat: number; maxLon: number; maxLat: number };
    /**
     * Regions-Cut-out (Alex 2026-08-15, Vorbild: Footprint-Maske der
     * GLB-Karte): Graustufen-PNG mercator-linear ueber bounds gerastert,
     * weiss = Region. Kacheln ganz ausserhalb werden NICHT gebaut,
     * Rand-Kacheln schneiden per AlphaMap entlang der Polygon-Grenze.
     */
    maskeUrl?: string;
    minZoom: number;
    maxZoom: number;
    /** Split-Schwelle: Kachelspanne/Distanz > Schwelle → teilen (Vorgabe 1.1) */
    splitSchwelle?: number;
    /** Sekunden zwischen LOD-Laeufen (Vorgabe 0.25) */
    lodIntervall?: number;
}

type KachelStatus = 'leer' | 'laedt' | 'bereit' | 'fehlt';

class Kachel {
    readonly addr: TileAddress;
    status: KachelStatus = 'leer';
    mesh: THREE.Mesh | null = null;
    hoehen: Hoehenfeld | null = null;
    kinder: Kachel[] | null = null;
    eltern: Kachel | null = null;
    /** Daten aus dem Eltern-Viertel geerbt (ausserhalb der Abdeckung) —
     * teilt die Eltern-Textur und darf nicht weiter geteilt werden. */
    synthetisch = false;
    /** Bricht laufende Loads ab, wenn die Kachel den Bedarf verlaesst (P0). */
    abbruch: AbortController | null = null;
    /** Engine-Welt-AABB (fuer Distanz + spaeter Frustum) */
    aabb = new THREE.Box3();
    zentrum = new THREE.Vector3();

    constructor(addr: TileAddress, eltern: Kachel | null = null) {
        this.addr = addr;
        this.eltern = eltern;
    }
}

/** Eltern-Quadrant bilinear auf ein volles Kind-Feld resampeln. */
function viertelFeld(eltern: Hoehenfeld, addr: TileAddress): Hoehenfeld {
    const n = eltern.aufloesung;
    const qx = addr.x & 1, qy = addr.y & 1;
    const daten = new Float32Array(n * n);
    let min = Infinity, max = -Infinity;
    for (let j = 0; j < n; j++) {
        const fy = (qy * (n - 1) + j) / 2;
        const y0 = Math.min(n - 2, Math.floor(fy)), ty = fy - y0;
        for (let i = 0; i < n; i++) {
            const fx = (qx * (n - 1) + i) / 2;
            const x0 = Math.min(n - 2, Math.floor(fx)), tx = fx - x0;
            const d = eltern.daten;
            const h = (d[y0 * n + x0] * (1 - tx) + d[y0 * n + x0 + 1] * tx) * (1 - ty)
                + (d[(y0 + 1) * n + x0] * (1 - tx) + d[(y0 + 1) * n + x0 + 1] * tx) * ty;
            daten[j * n + i] = h;
            if (h < min) min = h;
            if (h > max) max = h;
        }
    }
    return { daten, aufloesung: n, min, max };
}

export class TerrainRoot extends SceneNode {
    private readonly _cfg: Required<Omit<TerrainRootConfig, 'maskeUrl'>> & { maskeUrl?: string };
    private readonly _proj: MercatorProjection;
    private readonly _quelle: TerrainTileSource;
    private readonly _gruppe: THREE.Group;
    private _wurzeln: Kachel[] = [];
    private _lodUhr = 0;
    private _kamera: THREE.Camera | null = null;
    private readonly _hoehenListener = new Set<() => void>();
    private _hoehenGeaendert = false;
    /** feinste geladene Kachel je Mercator-Punkt findet die Hoehenabfrage hierueber */
    private _bereit = new Map<string, Kachel>();
    /** Regions-Maske: Canvas + Mercator-Fenster; Promise gate fuer die Lader */
    private _maske: { canvas: HTMLCanvasElement; minX: number; minY: number; maxX: number; maxY: number } | null = null;
    private _maskeBereit: Promise<void> = Promise.resolve();
    private readonly _materials = new TerrainMaterialManager();
    private _disposed = false;

    constructor(cfg: TerrainRootConfig) {
        super('TerrainRoot');
        /* splitSchwelle: teile, wenn Kachelspanne/Distanz die Schwelle reisst.
         * 0.8 ≈ Kachel projiziert auf ~420 px (65er-Mesh + 1024er-Textur
         * bleiben scharf). GROESSERE Werte = SPAETER teilen = groeber. */
        this._cfg = { splitSchwelle: 0.8, lodIntervall: 0.25, ...cfg };
        this._proj = cfg.projection;
        this._quelle = cfg.source;
        this._gruppe = new THREE.Group();
        this._gruppe.name = 'TerrainRoot';
        this._mesh = this._gruppe;
        this._mesh.userData.sceneNode = this;
        if (cfg.maskeUrl) this._ladeMaske(cfg.maskeUrl);
        this._baueWurzeln();
    }

    setCamera(kamera: THREE.Camera): void {
        this._kamera = kamera;
    }

    /** Shader-Link und lazy Diagnostik bewusst in die Ladephase ziehen. */
    warmupShaders(renderer: THREE.WebGLRenderer, scene: THREE.Scene, camera: THREE.Camera): Promise<void> {
        return this._materials.warmupShaders(renderer, scene, camera);
    }

    get projection(): MercatorProjection {
        return this._proj;
    }

    /**
     * Meldet gebündelt, wenn gestreamte Höhenfelder hinzugefügt oder entfernt
     * wurden. Verbraucher drapen daraufhin ihre Geometrie neu; pro LOD-Takt
     * wird höchstens eine Revision emittiert, auch wenn mehrere Tiles laden.
     */
    onHeightsChanged(listener: () => void): () => void {
        this._hoehenListener.add(listener);
        return () => this._hoehenListener.delete(listener);
    }

    protected calculateBoundingBox(): THREE.Box3 {
        const box = new THREE.Box3();
        for (const w of this._wurzeln) box.union(w.aabb);
        if (box.isEmpty()) box.setFromCenterAndSize(new THREE.Vector3(), new THREE.Vector3(1, 1, 1));
        return box;
    }

    // ── Hoehen-API (Ersatz fuer terrainLocalY) ──────────────────────────

    /** Hoehe in Metern am geo-Punkt; null wenn keine Kachel geladen. */
    getHeightMeters(lon: number, lat: number): number | null {
        const mx = MercatorProjection.lonToMercator(lon);
        const my = MercatorProjection.latToMercator(lat);
        for (let z = this._cfg.maxZoom; z >= this._cfg.minZoom; z--) {
            const t = MercatorProjection.tileAt(lon, lat, z);
            const k = this._bereit.get(`${t.z}/${t.x}/${t.y}`);
            if (!k || !k.hoehen) continue;
            return this._bilinear(k, mx, my);
        }
        return null;
    }

    /** Engine-Welt-Y am Welt-Punkt (x,z); null wenn keine Kachel geladen. */
    getWorldY(x: number, z: number): number | null {
        const { lon, lat } = this._proj.worldToGeo(x, z);
        const h = this.getHeightMeters(lon, lat);
        return h === null ? null : this._proj.hoeheToWorldY(h);
    }

    private _bilinear(k: Kachel, mx: number, my: number): number {
        const b = MercatorProjection.tileBoundsMercator(k.addr);
        const feld = k.hoehen!;
        const n = feld.aufloesung;
        const fx = ((mx - b.minX) / (b.maxX - b.minX)) * (n - 1);
        // Zeile 0 = Nordrand (maxY)
        const fy = ((b.maxY - my) / (b.maxY - b.minY)) * (n - 1);
        const x0 = Math.max(0, Math.min(n - 2, Math.floor(fx)));
        const y0 = Math.max(0, Math.min(n - 2, Math.floor(fy)));
        const tx = fx - x0, ty = fy - y0;
        const d = feld.daten;
        const h00 = d[y0 * n + x0], h10 = d[y0 * n + x0 + 1];
        const h01 = d[(y0 + 1) * n + x0], h11 = d[(y0 + 1) * n + x0 + 1];
        return (h00 * (1 - tx) + h10 * tx) * (1 - ty) + (h01 * (1 - tx) + h11 * tx) * ty;
    }

    // ── LOD-Lauf ────────────────────────────────────────────────────────

    protected onUpdate(deltaTime: number): void {
        this._lodUhr += deltaTime;
        if (this._lodUhr < this._cfg.lodIntervall) return;
        this._lodUhr = 0;
        if (this._kamera) {
            const kamPos = this._kamera.getWorldPosition(new THREE.Vector3());
            for (const w of this._wurzeln) this._lod(w, kamPos);
        }
        if (this._hoehenGeaendert) {
            this._hoehenGeaendert = false;
            for (const listener of [...this._hoehenListener]) listener();
        }
    }

    private _lod(k: Kachel, kamPos: THREE.Vector3): void {
        this._sichereGeladen(k);

        const dist = Math.max(0.001, k.aabb.distanceToPoint(kamPos));
        const spanne = this._proj.tileSpanWorld(k.addr.z);
        /* synthetische Kacheln (geerbte Eltern-Daten) weiter zu teilen
         * wuerde nur Aufloesung vortaeuschen und 404-Requests produzieren */
        const willTeilen = k.addr.z < this._cfg.maxZoom && !k.synthetisch
            && spanne / dist > this._cfg.splitSchwelle;

        /* Sichtbarkeit verwaltet JEDE Kachel ausschliesslich selbst:
         * Blatt → sichtbar; geteilt → nur sichtbar solange die Kinder noch
         * nicht alle bereit sind. Die Eltern duerfen die Kinder NIE
         * re-zeigen — sonst bleiben grobe Zwischenebenen ueber den feinen
         * liegen (der grosse Matsch-Bug der ersten Fassung). */
        if (willTeilen) {
            if (!k.kinder) k.kinder = this._erzeugeKinder(k);
            /* Variable Tiefe: fehlt EIN Kind (tiefere Stufe existiert dort
             * nicht — Datenrand oder z16 nur in der Schlucht-Insel), bleibt
             * die ELTERN-Kachel sichtbar und flickt die Luecke; die
             * VORHANDENEN Kinder verfeinern normal weiter. (Die erste
             * Fassung fror hier den ganzen Unterbaum ein — z7-Matsch lag
             * dauerhaft VOR den z15-Kacheln, Raycast-Befund 2026-08-15.) */
            if (k.kinder.some((x) => x.status === 'fehlt')) {
                this._zeige(k, k.status === 'bereit');
                for (const kind of k.kinder) {
                    if (kind.status !== 'fehlt') this._lod(kind, kamPos);
                }
                return;
            }
            for (const kind of k.kinder) this._lod(kind, kamPos);
            const alleBereit = k.kinder.every((x) => x.status === 'bereit');
            this._zeige(k, !alleBereit);
        } else {
            this._zeige(k, k.status === 'bereit');
            if (k.kinder) this._verwerfeUnterbaum(k);
        }
    }

    private _zeige(k: Kachel, sichtbar: boolean): void {
        if (k.mesh) k.mesh.visible = sichtbar;
    }

    private _verwerfeUnterbaum(k: Kachel): void {
        if (!k.kinder) return;
        for (const kind of k.kinder) {
            this._verwerfeUnterbaum(kind);
            this._entsorge(kind);
        }
        k.kinder = null;
    }

    private _entsorge(k: Kachel): void {
        k.abbruch?.abort();
        k.abbruch = null;
        if (k.mesh) {
            this._gruppe.remove(k.mesh);
            k.mesh.geometry.dispose();
            const mat = k.mesh.material as THREE.MeshStandardMaterial;
            // synthetisch = Eltern-Textur geteilt; Placeholder gehoert dem Manager
            this._materials.disposeMaterial(mat, !k.synthetisch);
            k.mesh = null;
        }
        k.synthetisch = false;
        if (this._bereit.delete(`${k.addr.z}/${k.addr.x}/${k.addr.y}`)) {
            this._hoehenGeaendert = true;
        }
        k.hoehen = null;
        if (k.status !== 'fehlt') k.status = 'leer';
    }

    private _erzeugeKinder(k: Kachel): Kachel[] {
        const { z, x, y } = k.addr;
        const kinder: Kachel[] = [];
        for (const [dx, dy] of [[0, 0], [1, 0], [0, 1], [1, 1]] as const) {
            const kind = new Kachel({ z: z + 1, x: x * 2 + dx, y: y * 2 + dy }, k);
            this._berechneAabb(kind);
            kinder.push(kind);
        }
        return kinder;
    }

    private _sichereGeladen(k: Kachel): void {
        if (k.status !== 'leer') return;
        k.status = 'laedt';
        k.abbruch = new AbortController();
        void this._ladeKachel(k);
    }

    private _ladeMaske(url: string): void {
        this._maskeBereit = new Promise((fertig) => {
            /* Der Cut-out ist Veredelung und darf NIE die Terrain-
             * Initialisierung blockieren — nach 8 s laeuft das Terrain ohne
             * Maske weiter. Laden via fetch statt Image(): Image-Loads
             * koennen unter Private-Network-Access STILL haengen (ohne
             * onload/onerror, beobachtet 2026-08-15/17); fetch schlaegt
             * ehrlich fehl und macht die Ursache im Log sichtbar. */
            const notAus = setTimeout(() => {
                if (!this._maske) {
                    console.warn('[terrain] Regionsmaske nicht ladbar (Timeout) — Terrain laeuft ohne Cut-out weiter:', url);
                    fertig();
                }
            }, 8000);
            fetch(url, { mode: 'cors' })
                .then((r) => (r.ok ? r.blob() : Promise.reject(new Error(`HTTP ${r.status}`))))
                .then((blob) => createImageBitmap(blob))
                .then((bmp) => {
                    clearTimeout(notAus);
                    const c = document.createElement('canvas');
                    c.width = bmp.width; c.height = bmp.height;
                    c.getContext('2d')!.drawImage(bmp, 0, 0);
                    bmp.close();
                    const b = this._cfg.bounds;
                    this._maske = {
                        canvas: c,
                        minX: MercatorProjection.lonToMercator(b.minLon),
                        maxX: MercatorProjection.lonToMercator(b.maxLon),
                        minY: MercatorProjection.latToMercator(b.minLat),
                        maxY: MercatorProjection.latToMercator(b.maxLat),
                    };
                    fertig();
                })
                .catch((e) => {
                    clearTimeout(notAus);
                    console.warn('[terrain] Regionsmaske nicht ladbar — Terrain laeuft ohne Cut-out weiter:', url, e);
                    fertig();
                });
        });
    }

    /**
     * Masken-Ausschnitt einer Kachel: 'aussen' (nichts bauen), 'innen'
     * (normal, keine AlphaMap noetig) oder Rand mit AlphaMap-Textur.
     */
    /** Klassifikations-Cache: 'aussen'/'innen' sind final; fuer Randkacheln
     *  wird das gerechnete Alpha-Canvas behalten und nur die (billige)
     *  Texture je Verwendung neu erzeugt — vorher lief das komplette
     *  drawImage+getImageData bei JEDEM Kachel-Load erneut (#4729 P4). */
    private _maskenCache = new Map<string, { art: 'aussen' | 'innen' } | { art: 'rand'; canvas: HTMLCanvasElement }>();

    private _maskeFuerKachel(k: Kachel): { art: 'aussen' | 'innen' } | { art: 'rand'; tex: THREE.Texture } {
        const m = this._maske;
        if (!m) return { art: 'innen' };
        const cacheKey = `${k.addr.z}/${k.addr.x}/${k.addr.y}`;
        const gecacht = this._maskenCache.get(cacheKey);
        if (gecacht) {
            if (gecacht.art !== 'rand') return gecacht;
            return { art: 'rand', tex: this._alsMaskenTextur(gecacht.canvas) };
        }
        const b = MercatorProjection.tileBoundsMercator(k.addr);
        const sx = ((b.minX - m.minX) / (m.maxX - m.minX)) * m.canvas.width;
        const sw = ((b.maxX - b.minX) / (m.maxX - m.minX)) * m.canvas.width;
        const sy = ((m.maxY - b.maxY) / (m.maxY - m.minY)) * m.canvas.height;   // Zeile 0 = Nord
        const sh = ((b.maxY - b.minY) / (m.maxY - m.minY)) * m.canvas.height;

        /* Alpha-Aufloesung mit der Kachelgroesse skalieren: eine z6-Kachel
         * ueberspannt die halbe Maske — 64 px waeren dort ein 4-km-Raster
         * und liessen Splitter an der Grenze stehen. */
        const n = Math.round(Math.min(256, Math.max(64, sw)));
        const c = document.createElement('canvas');
        c.width = c.height = n;
        const g = c.getContext('2d', { willReadFrequently: true })!;
        g.fillStyle = '#000';
        g.fillRect(0, 0, n, n);                 // ausserhalb des Maskenfensters = aussen
        g.imageSmoothingEnabled = true;
        /* Schnittrechteck MANUELL: Chrome klemmt out-of-bounds Source-Rects
         * bei drawImage und STRECKT die Randpixel, statt zu clippen — die
         * oberste (teils weisse) Maskenzeile wurde dadurch ueber den
         * Nord-Aussenbereich gestreckt (weisses Band, 2026-08-15). */
        const cx0 = Math.max(0, sx), cy0 = Math.max(0, sy);
        const cx1 = Math.min(m.canvas.width, sx + sw), cy1 = Math.min(m.canvas.height, sy + sh);
        if (cx1 <= cx0 || cy1 <= cy0) {
            this._maskenCache.set(cacheKey, { art: 'aussen' });
            return { art: 'aussen' };
        }
        g.drawImage(
            m.canvas,
            cx0, cy0, cx1 - cx0, cy1 - cy0,
            ((cx0 - sx) / sw) * n, ((cy0 - sy) / sh) * n,
            ((cx1 - cx0) / sw) * n, ((cy1 - cy0) / sh) * n,
        );
        const px = g.getImageData(0, 0, n, n).data;
        let weiss = 0, schwarz = 0;
        for (let i = 0; i < n * n; i++) {
            if (px[i * 4] > 200) weiss++;
            else if (px[i * 4] < 55) schwarz++;
        }
        if (weiss === n * n) {
            this._maskenCache.set(cacheKey, { art: 'innen' });
            return { art: 'innen' };
        }
        if (weiss === 0 && schwarz > n * n * 0.98) {
            this._maskenCache.set(cacheKey, { art: 'aussen' });
            return { art: 'aussen' };
        }
        this._maskenCache.set(cacheKey, { art: 'rand', canvas: c });
        return { art: 'rand', tex: this._alsMaskenTextur(c) };
    }

    private _alsMaskenTextur(c: HTMLCanvasElement): THREE.Texture {
        const tex = new THREE.CanvasTexture(c);
        /* flipY MUSS aus: unsere Kachel-UVs sind top-down (v=0 = Nord) wie
         * bei den Ortho-Texturen — CanvasTexture-Default flipY=true sampelt
         * die Maske sonst pro Kachel vertikal gespiegelt (weisses Band an
         * groben Randkacheln, 2026-08-15). */
        tex.flipY = false;
        tex.wrapS = tex.wrapT = THREE.ClampToEdgeWrapping;
        return tex;
    }

    private async _ladeKachel(k: Kachel): Promise<void> {
        await this._maskeBereit;
        const maske = this._maskeFuerKachel(k);
        if (maske.art === 'aussen') {
            /* Ausserhalb der Region: Kachel ist FERTIG-leer (kein Mesh, kein
             * Fetch). Bewusst 'bereit' statt 'fehlt' — sonst friert die
             * LOD-Randregel den Unterbaum ein (Lehre aus dem z7-Grau-Bug). */
            k.status = 'bereit';
            return;
        }
        const signal = k.abbruch?.signal;
        let [hoehen, ortho] = await Promise.all([
            this._quelle.ladeHoehen(k.addr, signal),
            this._quelle.ladeOrtho(k.addr, signal),
        ]);
        if (signal?.aborted) {
            // _entsorge hat die Kachel bereits zurueckgesetzt
            ortho?.tex.dispose();
            return;
        }
        let uvFenster: { u0: number; v0: number; spann: number } | null = null;
        let geteilteTextur: THREE.Texture | null = null;

        if (!hoehen) {
            /* Ausserhalb der Daten-Abdeckung: Hoehen + Textur-Viertel von der
             * ELTERN-Kachel erben (synthetisch). Damit gibt es im Sichtbar-
             * keitsbaum keine fehlt-Sonderfaelle mehr — die frueheren
             * Varianten (Loch / Eltern-flickt) ueberdeckten entweder nichts
             * oder das ganze Tal (z7-Grau-Vorfall 2026-08-15). */
            const e = k.eltern;
            if (e?.hoehen) {
                k.synthetisch = true;
                hoehen = viertelFeld(e.hoehen, k.addr);
                const elternMat = e.mesh?.material as THREE.MeshStandardMaterial | undefined;
                geteilteTextur = elternMat?.map ?? null;
                uvFenster = { u0: (k.addr.x & 1) * 0.5, v0: (k.addr.y & 1) * 0.5, spann: 0.5 };
                ortho?.tex.dispose();
                ortho = null;
            } else if (e && e.status !== 'fehlt') {
                // Eltern-Hoehen noch unterwegs → naechster LOD-Lauf probiert neu
                k.status = 'leer';
                ortho?.tex.dispose();
                return;
            } else {
                k.status = 'fehlt';
                ortho?.tex.dispose();
                return;
            }
        }
        k.hoehen = hoehen;
        /* Halb-Pixel-UV-Inset gegen Naht-Bleeding an Kachelraendern:
         * ClampToEdge allein reicht nicht, Mipmapping zieht sonst Farbe
         * der Nachbarpixel ueber die Kante (sichtbare vertikale Linien). */
        const uvInset = ortho ? 0.5 / ortho.groesse : 0;
        const geometrie = this._baueGeometrie(k, hoehen, uvInset, uvFenster);
        /* Rand-Kacheln: Cut-out entlang der Regionsgrenze via AlphaMap
         * (alphaTest statt transparent — kein Sortier-Overhead). Eine Map
         * ist IMMER gesetzt; bei Netzdruck dient 1x1 Weiss als Placeholder,
         * damit _orthoNachholen keinen USE_MAP-Programmwechsel ausloest. */
        const material = this._materials.createMaterial(
            ortho?.tex ?? geteilteTextur,
            maske.art === 'rand' ? maske.tex : null,
        );
        const mesh = new THREE.Mesh(geometrie, material);
        /* Ortho fehlgeschlagen (transienter Netzdruck beim Sub-Kachel-Sturm):
         * Kachel bleibt nutzbar, aber die Textur wird nachgeholt — sonst
         * steht eine permanent graue Flaeche im Tal (Vorfall 2026-08-15). */
        if (!ortho && !k.synthetisch) this._orthoNachholen(k, material, 1);
        mesh.name = `terrain/${k.addr.z}/${k.addr.x}/${k.addr.y}`;
        mesh.visible = false;                 // der naechste LOD-Lauf entscheidet
        mesh.matrixAutoUpdate = false;        // Kacheln bewegen sich nie
        mesh.updateMatrix();
        this._gruppe.add(mesh);
        k.mesh = mesh;
        this._berechneAabb(k);
        k.status = 'bereit';
        this._bereit.set(`${k.addr.z}/${k.addr.x}/${k.addr.y}`, k);
        this._hoehenGeaendert = true;
    }

    protected override onDestroy(): void {
        this._hoehenListener.clear();
        this.dispose();
    }

    private _orthoNachholen(k: Kachel, material: THREE.MeshStandardMaterial, versuch: number): void {
        if (versuch > 4) return;
        setTimeout(async () => {
            if (!k.mesh) return;                       // Kachel inzwischen entsorgt
            const ortho = await this._quelle.ladeOrtho(k.addr, k.abbruch?.signal);
            if (!k.mesh) { ortho?.tex.dispose(); return; }
            if (ortho) {
                this._materials.replaceMap(material, ortho.tex);
            } else {
                this._orthoNachholen(k, material, versuch + 1);
            }
        }, 2000 * versuch);
    }

    /**
     * Kachel-Geometrie in WELT-Koordinaten (Group sitzt im Ursprung).
     * Skirt: eine zusaetzliche Randreihe, um spanne*0.04 abgesenkt —
     * verdeckt Risse zwischen benachbarten LOD-Stufen.
     */
    private _baueGeometrie(
        k: Kachel,
        feld: Hoehenfeld,
        uvInset = 0,
        uvFenster: { u0: number; v0: number; spann: number } | null = null,
    ): THREE.BufferGeometry {
        const n = feld.aufloesung;
        const b = MercatorProjection.tileBoundsMercator(k.addr);
        const gesamt = n + 2;                                    // + Skirt-Ring
        const positionen = new Float32Array(gesamt * gesamt * 3);
        const normalen = new Float32Array(gesamt * gesamt * 3);
        const uvs = new Float32Array(gesamt * gesamt * 2);
        const spanne = this._proj.tileSpanWorld(k.addr.z);
        const skirtTiefe = spanne * 0.01;

        for (let gy = 0; gy < gesamt; gy++) {
            const iy = Math.max(0, Math.min(n - 1, gy - 1));
            const istSkirtY = gy === 0 || gy === gesamt - 1;
            for (let gx = 0; gx < gesamt; gx++) {
                const ix = Math.max(0, Math.min(n - 1, gx - 1));
                const istSkirt = istSkirtY || gx === 0 || gx === gesamt - 1;
                const mx = b.minX + ((b.maxX - b.minX) * ix) / (n - 1);
                const my = b.maxY - ((b.maxY - b.minY) * iy) / (n - 1);
                const h = feld.daten[iy * n + ix];
                const w = this._proj.mercatorToWorld(mx, my, h);
                const o = (gy * gesamt + gx) * 3;
                positionen[o] = w.x;
                positionen[o + 1] = w.y - (istSkirt ? skirtTiefe : 0);
                positionen[o + 2] = w.z;
                const uo = (gy * gesamt + gx) * 2;
                /* uvFenster: synthetische Kacheln teilen die Eltern-Textur
                 * und mappen nur deren Quadranten */
                const u0 = uvFenster ? uvFenster.u0 : uvInset;
                const v0 = uvFenster ? uvFenster.v0 : uvInset;
                const spannweite = uvFenster ? uvFenster.spann : 1 - 2 * uvInset;
                uvs[uo] = u0 + (ix / (n - 1)) * spannweite;
                uvs[uo + 1] = v0 + (iy / (n - 1)) * spannweite;        // v=0 Nord, flipY=false

                /* Normale ANALYTISCH aus dem Hoehenfeld (zentrale Differenz).
                 * computeVertexNormals wuerde die vertikalen Skirt-Waende in
                 * die Kanten-Normalen einrechnen — jede Kachelkante bekaeme
                 * kippende Beleuchtung und wuerde als Linie sichtbar (der
                 * Seam, den Alex am iPhone gemeldet hat). Skirt-Vertices
                 * erben die Normale ihrer Kante (istSkirt: ix/iy geklemmt). */
                const schritt = spanne / (n - 1);
                const yFaktor = this._proj.unitsPerMeter * this._proj.verticalExaggeration;
                const hL = feld.daten[iy * n + Math.max(0, ix - 1)];
                const hR = feld.daten[iy * n + Math.min(n - 1, ix + 1)];
                const hN = feld.daten[Math.max(0, iy - 1) * n + ix];
                const hS = feld.daten[Math.min(n - 1, iy + 1) * n + ix];
                const teilerX = (Math.min(n - 1, ix + 1) - Math.max(0, ix - 1)) * schritt;
                const teilerZ = (Math.min(n - 1, iy + 1) - Math.max(0, iy - 1)) * schritt;
                const dhdx = ((hR - hL) * yFaktor) / teilerX;
                const dhdz = ((hS - hN) * yFaktor) / teilerZ;   // iy+ = Sued = +z
                const laenge = Math.hypot(dhdx, 1, dhdz);
                normalen[o] = -dhdx / laenge;
                normalen[o + 1] = 1 / laenge;
                normalen[o + 2] = -dhdz / laenge;
            }
        }

        const indizes = new Uint32Array((gesamt - 1) * (gesamt - 1) * 6);
        let idx = 0;
        for (let gy = 0; gy < gesamt - 1; gy++) {
            for (let gx = 0; gx < gesamt - 1; gx++) {
                const a = gy * gesamt + gx;
                const c = a + gesamt;
                indizes[idx++] = a; indizes[idx++] = c; indizes[idx++] = a + 1;
                indizes[idx++] = a + 1; indizes[idx++] = c; indizes[idx++] = c + 1;
            }
        }

        const geo = new THREE.BufferGeometry();
        geo.setAttribute('position', new THREE.BufferAttribute(positionen, 3));
        geo.setAttribute('normal', new THREE.BufferAttribute(normalen, 3));
        geo.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
        geo.setIndex(new THREE.BufferAttribute(indizes, 1));
        geo.computeBoundingSphere();
        return geo;
    }

    private _berechneAabb(k: Kachel): void {
        const b = MercatorProjection.tileBoundsMercator(k.addr);
        const minH = k.hoehen?.min ?? 0;
        const maxH = k.hoehen?.max ?? 3800;
        const sw = this._proj.mercatorToWorld(b.minX, b.minY, minH);
        const ne = this._proj.mercatorToWorld(b.maxX, b.maxY, maxH);
        k.aabb.min.set(Math.min(sw.x, ne.x), Math.min(sw.y, ne.y), Math.min(sw.z, ne.z));
        k.aabb.max.set(Math.max(sw.x, ne.x), Math.max(sw.y, ne.y), Math.max(sw.z, ne.z));
        k.aabb.getCenter(k.zentrum);
    }

    private _baueWurzeln(): void {
        const { minLon, minLat, maxLon, maxLat } = this._cfg.bounds;
        const z = this._cfg.minZoom;
        const nw = MercatorProjection.tileAt(minLon, maxLat, z);
        const so = MercatorProjection.tileAt(maxLon, minLat, z);
        for (let y = nw.y; y <= so.y; y++) {
            for (let x = nw.x; x <= so.x; x++) {
                const k = new Kachel({ z, x, y });
                this._berechneAabb(k);
                this._wurzeln.push(k);
            }
        }
    }

    dispose(): void {
        if (this._disposed) return;
        this._disposed = true;
        for (const w of this._wurzeln) {
            this._verwerfeUnterbaum(w);
            this._entsorge(w);
        }
        this._materials.dispose();
    }
}
